import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, CalendarIcon, ExternalLinkIcon, NewspaperIcon, Share2Icon, CheckIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { BLOG_POSTS, BlogPost } from '../data/blog-posts';
import { Navbar } from '../components/Navbar';
const CATEGORY_COLORS: Record<string, string> = {
  Political: 'bg-haa-blue text-white',
  Immigration: 'bg-haa-skyblue text-white',
  Business: 'bg-emerald-600 text-white',
  'Note Worthy': 'bg-haa-midblue text-white',
  'Arts & Entertainment': 'bg-haa-navy text-white',
  Events: 'bg-blue-400 text-white',
  Sports: 'bg-orange-500 text-white'
};
function getCategoryColor(category: string) {
  return CATEGORY_COLORS[category] ?? 'bg-haa-blue text-white';
}
const postIdMap: Record<string, string> = {
  'post-41': '41',
  'post-40': '40',
  'post-1': '1',
  'post-2': '2',
  'post-5': '5',
  'post-6': '6',
  'post-7': '7',
  'post-8': '8',
  'post-9': '9',
  'post-10': '10',
  'post-11': '11',
  'post-12': '12',
  'post-13': '13',
  'post-14': '14',
  'post-15': '15',
  'post-16': '16',
  'post-17': '17',
  'post-18': '18',
  'post-19': '19',
  'post-20': '20',
  'post-21': '21',
  'post-22': '22',
  'post-23': '23',
  'post-24': '24',
  'post-25': '25',
  'post-26': '26',
  'post-27': '27',
  'post-28': '28',
  'post-29': '29',
  'post-30': '30',
  'post-31': '31',
  'post-32': '32',
  'post-33': '33',
  'post-34': '34',
  'post-35': '35',
  'post-36': '36',
  'post-37': '37'
};
export function NewsStoryPage({
  postId
}: {
  postId: string;
}) {
  const {
    t,
    language
  } = useLanguage();
  const isEs = language === 'ES';
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [postId]);
  // Keep URL hash in sync with current language so shared links preserve it
  useEffect(() => {
    const newHash = `#/news/${postId}?lang=${language}`;
    if (window.location.hash !== newHash) {
      window.history.replaceState(null, '', newHash);
    }
  }, [language, postId]);
  const post = BLOG_POSTS.find(p => p.id === postId);
  if (!post) {
    return <div className="font-nunito w-full min-h-screen overflow-x-hidden bg-gray-50" data-id="element-3594" data-mp="1omysgqbmdkai">
        <Navbar data-id="element-3595" data-mp="19u4zc23fd5nd" />
        <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center" data-id="element-3596" data-mp="1evc7415ki4u1">
          <h1 className="font-bebas text-4xl text-haa-navy tracking-wider mb-4" data-id="element-3597" data-mp="1et81nes0sxrn">
            {isEs ? 'Historia no encontrada' : 'Story Not Found'}
          </h1>
          <p className="font-nunito text-gray-500 mb-8" data-id="element-3598" data-mp="27iou2cfn4env">
            {isEs ? 'La historia que buscas no existe o ha sido movida.' : "The story you're looking for doesn't exist or has been moved."}
          </p>
          <a href="#" onClick={e => {
          e.preventDefault();
          window.location.hash = '';
        }} className="inline-flex items-center gap-2 bg-haa-blue text-white font-nunito font-bold text-sm px-6 py-3 rounded-full hover:bg-haa-midblue transition-colors" data-id="element-3599" data-mp="1xnn5bhpa1elt">
            <ArrowLeftIcon className="w-4 h-4" data-id="element-3600" data-mp="2vfuwbbck92pb" />
            {isEs ? 'Volver al Inicio' : 'Back to Home'}
          </a>
        </main>
      </div>;
  }
  const postNum = postIdMap[post.id] ?? '';
  const titleKey = postNum ? `news.post${postNum}.title` : '';
  const titleTranslation = titleKey ? t(titleKey) : '';
  const translatedTitle = titleKey && titleTranslation !== titleKey && titleTranslation !== '' ? titleTranslation : post.title;
  const dateKey = postNum ? `news.post${postNum}.date` : '';
  const dateTranslation = dateKey ? t(dateKey) : '';
  const translatedDate = dateKey && dateTranslation !== dateKey ? dateTranslation : post.date;
  const fullStoryKey = postNum ? `news.post${postNum}.fullStory` : '';
  const fullStoryTranslation = fullStoryKey ? t(fullStoryKey) : '';
  const translatedFullStory = fullStoryKey && fullStoryTranslation !== fullStoryKey ? fullStoryTranslation : post.fullStory;
  const categoryKeyMap: Record<string, string> = {
    Political: 'news.catPolitical',
    Immigration: 'news.catImmigration',
    Business: 'news.catBusiness',
    'Note Worthy': 'news.catNoteWorthy',
    'Arts & Entertainment': 'news.catArtMedia',
    Events: 'news.catEvents',
    Sports: 'news.catSports'
  };
  const translatedCategory = categoryKeyMap[post.category] ? t(categoryKeyMap[post.category]) : post.category;
  const handleCopyLink = () => {
    const base = window.location.origin + window.location.pathname;
    const url = `${base}#/news/${postId}?lang=${language}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return <div className="font-nunito w-full min-h-screen overflow-x-hidden bg-gray-50" data-id="element-3601" data-mp="14y7snveogtu4">
      <Navbar data-id="element-3602" data-mp="150c2etz9cn2b" />
      <main className="pt-24 pb-16" data-id="element-3603" data-mp="19xb01mi4rc59">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3604" data-mp="19ydlhewoomds">
          {/* Back link */}
          <motion.div initial={{
          opacity: 0,
          x: -10
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.3
        }} data-id="element-3605" data-mp="12euykxkbk8zx">
            <a href="#" onClick={e => {
            e.preventDefault();
            window.location.hash = '';
            setTimeout(() => {
              const el = document.getElementById('news');
              if (el) {
                el.scrollIntoView({
                  behavior: 'smooth'
                });
              }
            }, 200);
          }} className="inline-flex items-center gap-2 font-nunito text-haa-blue hover:text-haa-midblue text-sm font-semibold transition-colors mb-8" data-id="element-3606" data-mp="np5jdyfe64gn">
              <ArrowLeftIcon className="w-4 h-4" data-id="element-3607" data-mp="3r548m8m1coc4" />
              {isEs ? '← Volver a Noticias' : '← Back to News'}
            </a>
          </motion.div>

          {/* Image */}
          {post.image && <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} className="mb-8" data-id="element-3608" data-mp="2n8a84j296ldq">
              <img src={post.image} alt={translatedTitle} className="w-full max-h-[28rem] object-cover rounded-2xl shadow-lg" style={{
            objectPosition: post.imagePosition || 'center center'
          }} data-id="element-3609" data-mp="1zhec19qrl40o" />
            </motion.div>}

          {/* Video Embeds — shown at top when no image */}
          {!post.image && post.videoEmbeds && post.videoEmbeds.length > 0 && <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} className="mb-8" data-id="element-3610" data-mp="1lhdads2odsk5">
              {post.videoEmbeds.map((video, vi) => <div key={vi} className="rounded-2xl overflow-hidden shadow-lg bg-black" data-id="element-3611" data-mp="16vg7n4drbj5m" data-mpi={vi}>
                  {video.youtubeId ? <div className="relative w-full" style={{
              paddingBottom: '56.25%'
            }} data-id="element-3612" data-mp="1gu5cnwkk194g">
                      <iframe src={`https://www.youtube.com/embed/${video.youtubeId}`} title={video.label} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="absolute inset-0 w-full h-full" style={{
                border: 0
              }} data-id="element-3613" data-mp="1plp8a9twb2v7" />
                    </div> : null}
                </div>)}
            </motion.div>}

          {/* Category + Date */}
          <motion.div initial={{
          opacity: 0,
          y: 15
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.4,
          delay: 0.1
        }} className="flex flex-wrap items-center gap-3 mb-5" data-id="element-3614" data-mp="z38tbdckmhhd">
            <span className={`font-nunito text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full ${getCategoryColor(post.category)}`} data-id="element-3615" data-mp="g7io7h9cd4z9">
              {translatedCategory}
            </span>
            <span className="flex items-center gap-1.5 text-gray-400 text-sm" data-id="element-3616" data-mp="3jtpvkdwpwup9">
              <CalendarIcon className="w-4 h-4 text-haa-blue" data-id="element-3617" data-mp="20z4qbl8u8t40" />
              <span className="font-nunito" data-id="element-3618" data-mp="1wyb6p42fi0gx">{translatedDate}</span>
            </span>
          </motion.div>

          {/* Title */}
          <motion.h1 initial={{
          opacity: 0,
          y: 15
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.15
        }} className="font-bebas text-3xl sm:text-4xl lg:text-5xl text-haa-navy tracking-wider leading-tight mb-8" data-id="element-3619" data-mp="1rygcaqjg2sr4">
            {translatedTitle}
          </motion.h1>

          {/* Full Story */}
          {translatedFullStory && <motion.div initial={{
          opacity: 0,
          y: 15
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="prose prose-lg max-w-none font-nunito text-gray-600 leading-relaxed whitespace-pre-line mb-10" data-id="element-3620" data-mp="148camopth32r">
              {translatedFullStory}
            </motion.div>}

          {/* Story Images Gallery */}
          {post.storyImages && post.storyImages.length > 0 && <motion.div initial={{
          opacity: 0,
          y: 15
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.25
        }} className="mb-10 space-y-4" data-id="element-3621" data-mp="2e3qx9j6ut956">
              {post.storyImages.map((img, idx) => <div key={idx} className="rounded-xl overflow-hidden shadow-md" data-id="element-3622" data-mp="2dg7c16o5puv" data-mpi={idx}>
                  <img src={img} alt={`${translatedTitle} — ${idx + 1}`} className="w-full h-auto object-cover" data-id="element-3623" data-mp="3tlaif66qs5fj" />
                </div>)}
            </motion.div>}

          {/* Video Embeds — shown after story when post has an image */}
          {post.image && post.videoEmbeds && post.videoEmbeds.length > 0 && <motion.div initial={{
          opacity: 0,
          y: 15
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.4,
          delay: 0.25
        }} className="mb-10" data-id="element-3624" data-mp="ydesw8uz4c6">
              <h2 className="font-bebas text-2xl text-haa-navy tracking-wider mb-4" data-id="element-3625" data-mp="jw6g5u3u367g">
                {isEs ? 'Videos' : 'Videos'}
              </h2>
              <div className="grid grid-cols-1 gap-4" data-id="element-3626" data-mp="17kz9rjwzh1yw">
                {post.videoEmbeds.map((video, vi) => <div key={vi} className="rounded-2xl overflow-hidden shadow-lg bg-black" data-id="element-3627" data-mp="1cdsbdyx5nu6r" data-mpi={vi}>
                    {video.youtubeId ? <div className="relative w-full" style={{
                paddingBottom: '56.25%'
              }} data-id="element-3628" data-mp="1h9whcrqlbt13">
                        <iframe src={`https://www.youtube.com/embed/${video.youtubeId}`} title={video.label} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="absolute inset-0 w-full h-full" style={{
                  border: 0
                }} data-id="element-3629" data-mp="18arexabwrfjn" />
                      </div> : null}
                    <div className="px-4 py-3 bg-gray-900" data-id="element-3630" data-mp="fkj5q7zco5h7">
                      <p className="font-nunito text-white/80 text-xs font-semibold truncate" data-id="element-3631" data-mp="2r7w5siysjz33">
                        {video.label}
                      </p>
                    </div>
                  </div>)}
              </div>
            </motion.div>}

          {/* Coverage Links */}
          {post.coverageLinks && post.coverageLinks.length > 0 && <motion.div initial={{
          opacity: 0,
          y: 15
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.4,
          delay: 0.25
        }} className="pt-6 border-t border-gray-200 mb-8" data-id="element-3632" data-mp="28h4z5p66umse">
              <div className="flex items-center gap-2 mb-3" data-id="element-3633" data-mp="1osvwumaxl0f6">
                <NewspaperIcon className="w-4 h-4 text-gray-400" data-id="element-3634" data-mp="1osco9w767odt" />
                <span className="font-nunito text-gray-500 text-sm uppercase tracking-wider font-bold" data-id="element-3635" data-mp="1tr772y551cxx">
                  {t('news.alsoCoveredBy')}
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-3" data-id="element-3636" data-mp="3rg2v48o1dihq">
                {post.coverageLinks.map((link, i) => <a key={i} href={link.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-white hover:bg-gray-100 font-nunito text-sm font-bold text-haa-midblue hover:text-haa-blue transition-colors border border-gray-200 shadow-sm" data-id="element-3637" data-mp="92lgqv0syjea" data-mpi={i}>
                    {link.label}
                    <ExternalLinkIcon className="w-3.5 h-3.5" data-id="element-3638" data-mp="nyugdxoluyr8" />
                  </a>)}
              </div>
            </motion.div>}

          {/* Share Button */}
          <motion.div initial={{
          opacity: 0,
          y: 15
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.4,
          delay: 0.3
        }} className="pt-6 border-t border-gray-200 flex items-center gap-4" data-id="element-3639" data-mp="2ttm0jb6otizo">
            <button onClick={handleCopyLink} className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-nunito font-bold text-sm transition-all duration-200 shadow-sm ${copied ? 'bg-emerald-500 text-white' : 'bg-white border border-gray-200 text-haa-blue hover:bg-haa-blue hover:text-white hover:border-haa-blue'}`} data-id="element-3640" data-mp="25w79bm0ddz6o">
              {copied ? <>
                  <CheckIcon className="w-4 h-4" data-id="element-3641" data-mp="1mzy13fc5g2ul" />
                  {isEs ? '¡Enlace copiado!' : 'Link copied!'}
                </> : <>
                  <Share2Icon className="w-4 h-4" data-id="element-3642" data-mp="3hc5rqghftxkk" />
                  {isEs ? 'Copiar enlace' : 'Copy link'}
                </>}
            </button>
          </motion.div>
        </div>
      </main>
    </div>;
}